import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Building2, Users, Sparkles } from "lucide-react";
import {
  superListUsers,
  superListSucursales,
  type UserRow,
  type SucursalRow,
} from "../../lib/api";
import { SucursalesPanel } from "./Sucursales";
import { UsuariosPanel } from "./Usuarios";
import Campanas from "./Campanas";

// Página "Organizacion" — unifica Sucursales + Usuarios en una sola vista
// con pestañas internas (reorg UX 2026-06). Antes eran dos páginas
// separadas en el sidebar que igual compartían el fetch de sucursales;
// acá hacemos UN solo Promise.all en el padre y repartimos por props,
// eliminando el doble fetch. Ambas pestañas son super_admin-only (el
// guard vive en AdminLayout vía SUPER_ONLY_PATHS).
type Tab = "sucursales" | "usuarios" | "campanas";
const TABS: { key: Tab; label: string; Icon: typeof Building2 }[] = [
  { key: "sucursales", label: "Sucursales", Icon: Building2 },
  { key: "usuarios", label: "Usuarios", Icon: Users },
  { key: "campanas", label: "Campañas", Icon: Sparkles },
];

export default function Organizacion() {
  // La tab activa se sincroniza con ?tab para soportar deep-link
  // (alguien puede compartir /admin/organizacion?tab=usuarios). Si el
  // valor del query es inválido o falta, caemos a "sucursales".
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab: Tab =
    tabParam === "usuarios"
      ? "usuarios"
      : tabParam === "campanas"
      ? "campanas"
      : "sucursales";

  function setTab(tab: Tab) {
    // replace para no llenar el historial con cada cambio de pestaña.
    setSearchParams({ tab }, { replace: true });
  }

  const [users, setUsers] = useState<UserRow[]>([]);
  const [sucursales, setSucursales] = useState<SucursalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch unificado: ambas pestañas necesitan la lista de sucursales y
  // Usuarios además la de usuarios. Un solo Promise.all evita el doble
  // pedido a superListSucursales que había cuando eran páginas aparte.
  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const [us, suc] = await Promise.all([
        superListUsers(),
        superListSucursales(),
      ]);
      setUsers(us);
      setSucursales(suc);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <div>
      <h1 className="sr-only">Administración</h1>

      {/* Pestañas — mismo estilo que las de Ajustes del shopping
          (GlobalSettings): border-b-2 violeta en la activa. */}
      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-white/10">
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition ${
              activeTab === key
                ? "border-[#a78bfa] text-white"
                : "border-transparent text-white/60 hover:text-white"
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-500/15 p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      {activeTab === "campanas" ? (
        // Campanas se auto-fetchea (no usa el Promise.all de esta vista).
        <Campanas />
      ) : loading ? (
        <Loader2 className="h-8 w-8 animate-spin opacity-50" />
      ) : activeTab === "sucursales" ? (
        <SucursalesPanel sucursales={sucursales} refetch={refetch} />
      ) : (
        <UsuariosPanel
          users={users}
          sucursales={sucursales}
          refetch={refetch}
        />
      )}
    </div>
  );
}
